import * as React from "react";
import { Chip, ChipProps } from "./chip";

export interface ChipGroupProps {
  children: React.ReactNode;
  groupId: string;
  defaultActiveChip?: number;
  activeChip?: number; // Add controlled active chip prop
  multiple?: boolean; // Add multiple selection support
  onGroupChange?: (groupId: string, chipIndex: number) => void;
  className?: string;
}

export const ChipGroup: React.FC<ChipGroupProps> = ({
  children,
  groupId,
  defaultActiveChip = 0,
  activeChip, // Add controlled active chip prop
  multiple = false, // Default to single selection
  onGroupChange,
  className,
}) => {
  // State for single selection (default behavior)
  const [internalActiveChipIndex, setInternalActiveChipIndex] =
    React.useState<number>(defaultActiveChip);

  // Use controlled activeChip if provided, otherwise use internal state
  const activeChipIndex =
    activeChip !== undefined ? activeChip : internalActiveChipIndex;

  // Sync internal state when external activeChip changes
  React.useEffect(() => {
    if (activeChip !== undefined) {
      setInternalActiveChipIndex(activeChip);
    }
  }, [activeChip]);

  // State for multiple selection
  const [activeChipIndices, setActiveChipIndices] = React.useState<Set<number>>(
    multiple ? new Set([defaultActiveChip]) : new Set()
  );

  const handleGroupChange = React.useCallback(
    (groupId: string, chipIndex: number) => {
      if (multiple) {
        // For multiple selection, toggle the chip
        setActiveChipIndices((prev) => {
          const newSet = new Set(prev);
          if (newSet.has(chipIndex)) {
            newSet.delete(chipIndex);
          } else {
            newSet.add(chipIndex);
          }
          return newSet;
        });
      } else {
        // For single selection, only update internal state if not controlled externally
        if (activeChip === undefined) {
          setInternalActiveChipIndex(chipIndex);
        }
      }
      onGroupChange?.(groupId, chipIndex);
    },
    [multiple, onGroupChange, activeChip]
  );

  // Clone children and inject group props
  const enhancedChildren = React.Children.map(children, (child, index) => {
    if (React.isValidElement(child) && child.type === Chip) {
      // Find the actual chip index by counting only Chip components
      const chipIndex = React.Children.toArray(children)
        .slice(0, index)
        .filter(
          (child) => React.isValidElement(child) && child.type === Chip
        ).length;

      const isActive = multiple
        ? activeChipIndices.has(chipIndex)
        : chipIndex === activeChipIndex;

      return React.cloneElement(child as React.ReactElement<ChipProps>, {
        groupId,
        isActive,
        onGroupChange: handleGroupChange,
        chipIndex: chipIndex,
        toggled: true, // Force toggled behavior for group chips
      });
    }
    return child;
  });

  return (
    <div className={className} data-chip-group={groupId}>
      {enhancedChildren}
    </div>
  );
};
